import * as t from 'io-ts';
import _ from 'lodash';

import {makeEnumRuntimeType} from './apiTypes';
import {ConversationTeamStatusesEnum} from './conversationCommonTypes';
import {RConversationTrackerStage, RConversationTrackerStatus} from './conversationTrackerModel';

/*
 * Runtime type.
 */

const RConversationTrackerPatchBase = t.partial({
  status: RConversationTrackerStatus,
  stage: RConversationTrackerStage,
  bump: t.boolean,
  bumpedAt: t.number,
  remindAt: t.union([t.number, t.null]),
});

const RConversationTrackerPatchId = t.intersection([
  RConversationTrackerPatchBase,
  t.type({
    teammateId: t.number,
  }),
]);

const RConversationTrackerPatchEmail = t.intersection([
  RConversationTrackerPatchBase,
  t.type({
    teammateEmail: t.string,
  }),
]);

// TODO: Merge with RConversationTracker.
const RConversationTrackerPatch = t.union([RConversationTrackerPatchId, RConversationTrackerPatchEmail]);

const RConversationTimelinePatch = t.intersection([
  t.type({
    type: t.string,
    uid: t.string,
  }),
  t.partial({
    meta: t.partial({
      delay: t.number,
    }),
  }),
]);

const RConversationTopicsPatch = t.readonly(
  t.type({
    id: t.number,
    name: t.string,
  }),
);

const RConversationPatchDirty = t.readonly(
  t.partial({
    assigneeId: t.union([t.number, t.null]),
    teamStatus: makeEnumRuntimeType(ConversationTeamStatusesEnum),
    inboxId: t.number,
    retainTrackerAccessOnMove: t.boolean,
    subject: t.string,
    addTrackers: t.readonlyArray(RConversationTrackerPatch),
    updateTrackers: t.readonlyArray(RConversationTrackerPatch),
    removeTrackers: t.readonlyArray(t.number),
    addTags: t.readonlyArray(t.number),
    removeTags: t.readonlyArray(t.number),
    pinActivities: t.readonlyArray(t.number),
    unpinActivities: t.readonlyArray(t.number),
    addTopics: t.readonlyArray(t.number),
    removeTopics: t.readonlyArray(t.number),
    updateTopics: t.readonlyArray(RConversationTopicsPatch),
    remindAt: t.union([t.number, t.null]),
    isSeen: t.boolean,
    seenAt: t.number,
    addTimeline: t.readonlyArray(RConversationTimelinePatch),
    // TODO: Bring offlineMetadata into io-ts type.
  }),
  'conversationPatch',
);

/*
 * Type.
 */

interface ConversationTrackerPatchIdModel extends t.TypeOf<typeof RConversationTrackerPatchId> {}
interface ConversationTrackerPatchEmailModel extends t.TypeOf<typeof RConversationTrackerPatchEmail> {}
type ConversationTrackerPatchModel = ConversationTrackerPatchIdModel | ConversationTrackerPatchEmailModel;
export interface ConversationPatchModel extends t.TypeOf<typeof RConversationPatchDirty> {}
export const RConversationPatch = t.clean<ConversationPatchModel>(RConversationPatchDirty as any);

export interface ConversationPatch extends ConversationPatchModel {
  offlineMetadata?: ConversationOfflineMetadata;
}

/** Metadata for conflict resolution of offline actions. */
interface ConversationOfflineMetadata {
  trackers?: ReadonlyArray<ConversationTrackerOfflineMetadata>;
  /** When the offline action was initiated by the user. */
  initiatedAt?: number;
}

interface ConversationTrackerOfflineMetadataBase {
  teammateId: number;
}

/** Metadata for conflict resolution of offline tracker bump actions. */
interface ConversationTrackerBumpOfflineMetadata extends ConversationTrackerOfflineMetadataBase {
  /** The last known bump date to start the offline transaction. */
  lastBumpedAt: number;
  /** The offline transaction ID. */
  offlineBumpedId: string;
}

/** Metadata for conflict resolution of offline tracker seen actions. */
interface ConversationTrackerSeenOfflineMetadata extends ConversationTrackerOfflineMetadataBase {
  /** The last known seen update date to start the offline transaction. */
  lastSeenUpdatedAt: number | undefined;
  /** The last known activity date to start the offline transaction. */
  lastActivityAt: number;
  /** The offline transaction ID. */
  offlineSeenId: string;
}

/** Metadata for conflict resolution of offline tracker actions. */
type ConversationTrackerOfflineMetadata =
  | ConversationTrackerBumpOfflineMetadata
  | ConversationTrackerSeenOfflineMetadata;

export type ConversationPatchBuilder = (conversationId: number) => ConversationPatch | undefined;

/*
 * IO methods.
 */

export function parseConversationPatch(src: any): ConversationPatchModel {
  return {
    assigneeId: src.assignee_id,
    teamStatus: src.status || undefined,
    inboxId: src.inbox_id || undefined,
    addTrackers: src.trackers?.add?.map((tracker: any) => parseConversationTracker(tracker)),
    updateTrackers: src.trackers?.update?.map((tracker: any) => parseConversationTracker(tracker)),
    removeTrackers: src.trackers?.remove,
    addTags: src.tags?.add,
    removeTags: src.tags?.remove,
    pinActivities: [],
    unpinActivities: [],
    addTopics: [],
    removeTopics: [],
    updateTopics: [],
    remindAt: src.reminder,
    isSeen: src.is_seen,
    seenAt: src.seen_at,
    addTimeline: [],
  };
}

export function parseConversationTracker(src: any): ConversationTrackerPatchModel {
  const baseTracker = {
    status: src.status,
    stage: src.stage,
    bump: src.bump,
    bumpedAt: src.bumped_at,
    remindAt: src.reminder,
  };

  if (src.teammate_email) {
    return {
      ...baseTracker,
      teammateEmail: src.teammate_email,
    };
  }

  return {
    ...baseTracker,
    teammateId: src.teammate_id,
  };
}

export function serializePatch(patch: ConversationPatch) {
  const hasNewMove = _.isBoolean(patch.retainTrackerAccessOnMove);
  return {
    assignee_id: patch.assigneeId,
    status: patch.teamStatus,
    inbox_id: hasNewMove ? undefined : patch.inboxId,
    move_to_inbox: hasNewMove
      ? {
          inbox_id: patch.inboxId,
          retain_trackers_access: patch.retainTrackerAccessOnMove,
        }
      : undefined,
    subject: patch.subject,
    trackers: {
      add: patch.addTrackers ? patch.addTrackers.map(serializeConversationTracker) : undefined,
      update: patch.updateTrackers ? patch.updateTrackers.map(serializeConversationTracker) : undefined,
      remove: patch.removeTrackers ? patch.removeTrackers.map((id) => ({teammate_id: id})) : undefined,
    },
    tags: {
      add: patch.addTags,
      remove: patch.removeTags,
    },
    pinnedActivities: {
      add: patch.pinActivities,
      remove: patch.unpinActivities,
    },
    topics: {
      add: patch.addTopics,
      remove: patch.removeTopics,
      update: patch.updateTopics,
    },
    reminder: patch.remindAt,
    is_seen: patch.isSeen,
    seen_at: patch.seenAt,
    timeline: {
      add: patch.addTimeline,
    },
    offline: patch.offlineMetadata && serializeConversationOfflineMetadata(patch.offlineMetadata),
  };
}

export function serializeConversationPatch(conversationId: number, patch: ConversationPatch) {
  return {
    id: conversationId,
    ...serializePatch(patch),
  };
}

export function serializeConversationTracker(trackerPatch: ConversationTrackerPatchModel) {
  const patch = {
    status: trackerPatch.status,
    stage: trackerPatch.stage,
    bump: trackerPatch.bump,
    bumped_at: trackerPatch.bumpedAt,
    reminder: trackerPatch.remindAt,
  };

  return 'teammateId' in trackerPatch
    ? {...patch, teammate_id: trackerPatch.teammateId}
    : {...patch, teammate_email: trackerPatch.teammateEmail};
}

export function serializeConversationOfflineMetadata(metadata: ConversationOfflineMetadata) {
  return {
    trackers: metadata.trackers && metadata.trackers.map(serializeConversationTrackerOfflineMetadata),
    offline_ts: metadata.initiatedAt,
  };
}

function serializeConversationTrackerOfflineMetadata(metadata: ConversationTrackerOfflineMetadata) {
  if ('offlineSeenId' in metadata) {
    return {
      teammate_id: metadata.teammateId,
      last_seen_updated_at: metadata.lastSeenUpdatedAt,
      last_activity_at: metadata.lastActivityAt,
      offline_seen_id: metadata.offlineSeenId,
    };
  }

  return {
    teammate_id: metadata.teammateId,
    last_bumped_at: metadata.lastBumpedAt,
    offline_bumped_id: metadata.offlineBumpedId,
  };
}
